(() => {
  const SETTINGS = {
    debug: false,
    editLabel: 'Edit',
    quantityLabel: 'Qty:',
    updateDelay: 80
  };

  const SELECTORS = {
    bundle: '.rebuy-cart__flyout-item-bundle',
    cartItem: '.rebuy-cart__flyout-item',
    itemInfo: '.rebuy-cart__flyout-item-info',
    productTitle: '.rebuy-cart__flyout-item-product-title',
    variantTitle: '.rebuy-cart__flyout-item-variant-title',
    bundleButton: '.rebuy-cart__flyout-item-bundle-toggle-button',
    bundleChild: '.rebuy-cart__flyout-item-bundle-child',
    bundleChildTitle: '.rebuy-cart__flyout-item-bundle-title',
    bundleChildVariant: '.rebuy-cart__flyout-item-bundle-value'
  };

  const productCache = new Map();

  let updateTimer = null;
  let updateRunning = false;
  let updateRequestedAgain = false;
  let observer = null;

  const log = (...values) => {
    if (SETTINGS.debug) {
      console.log('[BentoSMB Rebuy Bundle]', ...values);
    }
  };

  const normalize = value =>
    String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  const normalizeVariantTitle = value => {
    const normalized = normalize(value)
        .replace(/\s+x\s+\d+\s*$/i, '')
        .trim();

    return normalized === 'default title'
        ? ''
        : normalized;
    };

  const getProperties = item => {
    const properties = item?.properties;

    if (!properties) {
      return {};
    }

    if (!Array.isArray(properties)) {
      return properties;
    }

    return Object.fromEntries(
      properties.map(property => {
        if (Array.isArray(property)) {
          return [property[0], property[1]];
        }

        return [
          property?.key ?? property?.name ?? '',
          property?.value ?? ''
        ];
      })
    );
  };

  const parseSelection = value => {
    const rawValue = String(value || '').trim();
    const separatorIndex = rawValue.lastIndexOf('__');

    if (separatorIndex <= 0) {
      return null;
    }

    const handle = rawValue.slice(0, separatorIndex);
    const variantId = rawValue.slice(separatorIndex + 2);

    if (!handle || !variantId) {
      return null;
    }

    return {
      handle,
      variantId: String(variantId)
    };
  };

  /**
   * Parses:
   *
   * v=2;
   * chain=the-custom-bracelet__51501844070706;
   * charms=handle__variant,handle__variant,...
   */
  const parseConfig = item => {
    const properties = getProperties(item);
    const config = String(properties.__config || '').trim();

    if (!config) {
      return null;
    }

    const params = new URLSearchParams(
      config.replaceAll(';', '&')
    );

    if (params.get('v') !== '2') {
      return null;
    }

    const grouped = new Map();

    const addSelection = selection => {
      if (!selection) {
        return;
      }

      const key = selection.variantId;
      const existing = grouped.get(key);

      if (existing) {
        existing.quantity += 1;
        return;
      }

      grouped.set(key, {
        ...selection,
        quantity: 1
      });
    };

    addSelection(
      parseSelection(params.get('chain'))
    );

    String(params.get('charms') || '')
      .split(',')
      .map(parseSelection)
      .filter(Boolean)
      .forEach(addSelection);

    return {
      raw: config,
      items: Array.from(grouped.values())
    };
  };

  /**
   * Reads MW's underlying component quantities.
   *
   * This is preferred over counting Rebuy's rendered children because
   * Rebuy combines repeated variants into one visible child.
   */
  const parseMwBuild = item => {
    const properties = getProperties(item);
    const rawBuild = properties.__mw_build;

    if (!rawBuild) {
      return null;
    }

    try {
      const build =
        typeof rawBuild === 'string'
          ? JSON.parse(rawBuild)
          : rawBuild;

      if (!Array.isArray(build?.items)) {
        return null;
      }

      const quantities = new Map();

      build.items.forEach(component => {
        const variantId = String(
          component?.variant_id || ''
        );

        const parsedQuantity = Number(component?.qty);
        const quantity =
          Number.isFinite(parsedQuantity) &&
          parsedQuantity > 0
            ? parsedQuantity
            : 1;

        if (!variantId) {
          return;
        }

        quantities.set(
          variantId,
          (quantities.get(variantId) || 0) + quantity
        );
      });

      return {
        quantities,
        totalQuantity: Array.from(
          quantities.values()
        ).reduce(
          (total, quantity) => total + quantity,
          0
        )
      };
    } catch (error) {
      console.warn(
        '[BentoSMB Rebuy Bundle] Could not parse __mw_build.',
        error
      );

      return null;
    }
  };

  const getBundleData = item => {
    const config = parseConfig(item);

    if (!config?.items.length) {
      return null;
    }

    const mwBuild = parseMwBuild(item);

    const items = config.items.map(component => ({
      ...component,
      quantity:
        mwBuild?.quantities.get(component.variantId) ??
        component.quantity
    }));

    const totalQuantity = items.reduce(
      (total, component) =>
        total + component.quantity,
      0
    );

    return {
      items,
      totalQuantity
    };
  };

  const fetchProduct = handle => {
    if (!productCache.has(handle)) {
      const request = fetch(
        `/products/${encodeURIComponent(handle)}.js`,
        {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json'
          }
        }
      )
        .then(response => {
          if (!response.ok) {
            throw new Error(
              `Product request failed: ${response.status}`
            );
          }

          return response.json();
        })
        .catch(error => {
          console.warn(
            `[BentoSMB Rebuy Bundle] Could not load product "${handle}".`,
            error
          );

          return null;
        });

      productCache.set(handle, request);
    }

    return productCache.get(handle);
  };

  const hydrateComponent = async component => {
    const product = await fetchProduct(component.handle);

    const variant = product?.variants?.find(
      option =>
        String(option.id) === component.variantId
    );

    return {
      ...component,
      productTitle:
        product?.title ||
        component.handle
          .replace(/[-_]+/g, ' ')
          .replace(/\b\w/g, character =>
            character.toUpperCase()
          ),
      variantTitle:
        variant?.title === 'Default Title'
          ? ''
          : variant?.title || ''
    };
  };

  const getBundleCartItems = cart =>
    cart.items
      .map(item => ({
        item,
        bundle: getBundleData(item)
      }))
      .filter(entry => entry.bundle);

  const findCartEntry = ({
    bundleElement,
    cartEntries,
    usedEntries,
    bundleIndex
  }) => {
    const parentElement = bundleElement.closest(
      SELECTORS.cartItem
    );

    const displayedTitle = normalize(
      parentElement
        ?.querySelector(SELECTORS.productTitle)
        ?.textContent
    );

    let match = cartEntries.find(entry => {
      if (usedEntries.has(entry)) {
        return false;
      }

      return normalize(
        entry.item.product_title ||
        entry.item.title
      ) === displayedTitle;
    });

    if (!match) {
      match = cartEntries.find(
        entry => !usedEntries.has(entry)
      );
    }

    if (!match) {
      match = cartEntries[bundleIndex] || null;
    }

    if (match) {
      usedEntries.add(match);
    }

    return {
      parentElement,
      entry: match
    };
  };

  const updateBundleTotal = (
    bundleElement,
    totalQuantity
  ) => {
    const button = bundleElement.querySelector(
      SELECTORS.bundleButton
    );

    if (!button) {
      return;
    }

    const countElement = Array.from(
      button.children
    ).find(element => {
      return (
        element.tagName === 'SPAN' &&
        !element.classList.contains(
          'rebuy-cart__flyout-bundle-chev-icon'
        )
      );
    });

    if (
      countElement &&
      countElement.textContent !== String(totalQuantity)
    ) {
      countElement.textContent = String(totalQuantity);
    }

    button.childNodes.forEach(node => {
      if (node.nodeType !== Node.TEXT_NODE) {
        return;
      }

      if (!/\bitems?\b/i.test(node.textContent || '')) {
        return;
      }

      const replacement =
        totalQuantity === 1 ? 'item' : 'items';

      node.textContent = node.textContent.replace(
        /\bitems?\b/i,
        replacement
      );
    });
  };

  const findMatchingComponent = ({
    childElement,
    components,
    usedComponentIndexes
  }) => {
    const childTitle = normalize(
      childElement
        .querySelector(SELECTORS.bundleChildTitle)
        ?.textContent
    );

    const childVariant = normalizeVariantTitle(
      childElement
        .querySelector(SELECTORS.bundleChildVariant)
        ?.textContent
    );

    let componentIndex = components.findIndex(
      (component, index) => {
        if (usedComponentIndexes.has(index)) {
          return false;
        }

        return (
          normalize(component.productTitle) === childTitle &&
          normalizeVariantTitle(component.variantTitle) ===
            childVariant
        );
      }
    );

    /*
     * Fallback when Rebuy omits or formats a variant title
     * differently. Only use a title-only match when exactly one
     * unused component has that product title.
     */
    if (componentIndex === -1) {
      const titleMatches = components
        .map((component, index) => ({
          component,
          index
        }))
        .filter(({ component, index }) => {
          return (
            !usedComponentIndexes.has(index) &&
            normalize(component.productTitle) === childTitle
          );
        });

      if (titleMatches.length === 1) {
        componentIndex = titleMatches[0].index;
      }
    }

    if (componentIndex === -1) {
      return null;
    }

    usedComponentIndexes.add(componentIndex);

    return components[componentIndex];
  };

  const updateBundleChildren = async (
  bundleElement,
  bundleItems
) => {
  const components = await Promise.all(
    bundleItems.map(hydrateComponent)
  );

  const childElements = Array.from(
    bundleElement.querySelectorAll(
      SELECTORS.bundleChild
    )
  );

  const usedComponentIndexes = new Set();

  childElements.forEach(childElement => {
    const component = findMatchingComponent({
      childElement,
      components,
      usedComponentIndexes
    });

    if (!component) {
      log(
        'Could not match rendered bundle child:',
        childElement
      );

      return;
    }

    const rightColumn = childElement.querySelector(
      '.rebuy-cart__flyout-item-bundle-child-right'
    );

    if (!rightColumn) {
      return;
    }

    /*
     * Remove the previous standalone quantity element.
     */
    rightColumn
      .querySelectorAll(
        '.bentosmb-rebuy-bundle-quantity'
      )
      .forEach(element => element.remove());

    let variantElement = rightColumn.querySelector(
      SELECTORS.bundleChildVariant
    );

    /*
     * Some bundle components do not have a variant-value
     * element, so create one when necessary.
     */
    if (!variantElement) {
      variantElement = document.createElement('p');
      variantElement.className =
        'rebuy-cart__flyout-item-bundle-value';

      rightColumn.append(variantElement);
    }

    /*
     * Save the original value so repeated updates do not
     * produce "A x 3 x 3".
     */
    const existingBaseValue =
      variantElement.dataset.bentosmbBaseValue;

    const baseValue = (
      existingBaseValue ??
      variantElement.textContent ??
      ''
    )
      .replace(/\s+x\s+\d+\s*$/i, '')
      .trim();

    variantElement.dataset.bentosmbBaseValue =
      baseValue;

    variantElement.textContent = baseValue
      ? `${baseValue} x ${component.quantity}`
      : `x ${component.quantity}`;
  });
};

  const createEditURL = item => {
    if (!item?.handle || !item?.key) {
      return null;
    }

    const url = new URL(
      `/products/${item.handle}`,
      window.location.origin
    );

    url.searchParams.set('line', item.key);

    return `${url.pathname}${url.search}`;
  };

  const addEditButton = (parentElement, item) => {
  if (!parentElement) {
    return;
  }

  const editURL = createEditURL(item);

  if (!editURL) {
    return;
  }

  const itemInfo = parentElement.querySelector(
    SELECTORS.itemInfo
  );

  const removeButton = itemInfo?.querySelector(
    '.rebuy-cart__flyout-item-remove'
  );

  if (!itemInfo || !removeButton) {
    return;
  }

  let editButton = itemInfo.querySelector(
    '.bentosmb-rebuy-bundle-edit'
  );

  if (!editButton) {
    editButton = document.createElement('a');
    editButton.className =
      'bentosmb-rebuy-bundle-edit';
    editButton.textContent = SETTINGS.editLabel;
  }

  editButton.href = editURL;
  editButton.setAttribute(
    'aria-label',
    `Edit ${
      item.product_title ||
      item.title ||
      'bundle'
    }`
  );

  /*
   * This also moves an existing Edit link from its
   * previous location.
   */
  removeButton.parentNode.insertBefore(
    editButton,
    removeButton
  );
};

  const fetchCart = async () => {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(
        `Cart request failed: ${response.status}`
      );
    }

    return response.json();
  };

  const performUpdate = async () => {
    const bundleElements = Array.from(
      document.querySelectorAll(SELECTORS.bundle)
    );

    if (!bundleElements.length) {
      return;
    }

    const cart = await fetchCart();
    const cartEntries = getBundleCartItems(cart);
    const usedEntries = new Set();

    await Promise.all(
      bundleElements.map(
        async (bundleElement, bundleIndex) => {
          const {
            parentElement,
            entry
          } = findCartEntry({
            bundleElement,
            cartEntries,
            usedEntries,
            bundleIndex
          });

          if (!entry) {
            log(
              'No matching bundle cart item found.',
              bundleElement
            );

            return;
          }

          updateBundleTotal(
            bundleElement,
            entry.bundle.totalQuantity
          );

          addEditButton(
            parentElement,
            entry.item
          );

          await updateBundleChildren(
            bundleElement,
            entry.bundle.items
          );
        }
      )
    );
  };

  const runUpdate = async () => {
    if (updateRunning) {
      updateRequestedAgain = true;
      return;
    }

    updateRunning = true;

    try {
      do {
        updateRequestedAgain = false;
        await performUpdate();
      } while (updateRequestedAgain);
    } catch (error) {
      console.warn(
        '[BentoSMB Rebuy Bundle] Update failed.',
        error
      );
    } finally {
      updateRunning = false;
    }
  };

  const scheduleUpdate = (delay = SETTINGS.updateDelay) => {
    window.clearTimeout(updateTimer);

    updateTimer = window.setTimeout(
      runUpdate,
      delay
    );
  };

  const injectStyles = () => {
    if (
      document.getElementById(
        'bentosmb-rebuy-bundle-styles'
      )
    ) {
      return;
    }

    const style = document.createElement('style');

    style.id = 'bentosmb-rebuy-bundle-styles';
    style.textContent = `
  .rebuy-cart__flyout-item-info {
    position: relative;
  }

  .bentosmb-rebuy-bundle-edit {
    position: absolute;
    top: 0;
    right: 34px;
    margin: 0;
    font-size: 12px;
    line-height: 1.3;
    text-decoration: underline;
    text-underline-offset: 2px;
    z-index: 2;
  }
`;

    document.head.append(style);
  };

  const initialize = () => {
    injectStyles();

    if (!observer) {
      observer = new MutationObserver(mutations => {
        const rebuyChanged = mutations.some(mutation => {
          const target = mutation.target;

          return (
            target instanceof Element &&
            (
              target.closest('#rebuy-cart') ||
              target.matches('#rebuy-cart')
            )
          );
        });

        if (rebuyChanged) {
          scheduleUpdate();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    [
      'rebuy:smartcart.ready',
      'rebuy:smartcart.show',
      'rebuy:cart.ready',
      'rebuy:cart.change'
    ].forEach(eventName => {
      document.addEventListener(
        eventName,
        () => scheduleUpdate(20)
      );
    });

    scheduleUpdate(20);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }
})();